import { useCallback, useMemo } from "react";
import { useProjectSlugForRequests, useSdkClient } from "@/contexts/Sdk";

import type { DeploymentExternalMCP } from "@gram/client/models/components/deploymentexternalmcp.js";
import { ExternalMCPServerEntry } from "@gram/client/models/components/externalmcpserverentry.js";
import { ExternalMCPTool } from "@gram/client/models/components/externalmcptool.js";
import {
  catalogServerInstallState,
  liveCatalogExternalMcp,
  normalizedUrlSet,
  type CatalogInstallState,
} from "@/pages/catalog/catalogInstall";
import { queryKeyListMCPCatalog } from "@gram/client/react-query/listMCPCatalog.js";
import { useLatestDeployment } from "@gram/client/react-query/latestDeployment.js";
import { useMcpServers } from "@gram/client/react-query/mcpServers.js";
import { useQuery } from "@tanstack/react-query";
import { useRemoteMcpServers } from "@gram/client/react-query/remoteMcpServers.js";
import { useUnproxiedMcpServers } from "@gram/client/react-query/unproxiedMcpServers.js";

interface ServerMeta {
  "com.pulsemcp/server"?: {
    visitorsEstimateMostRecentWeek?: number;
    visitorsEstimateLastFourWeeks?: number;
    visitorsEstimateTotal?: number;
    isOfficial?: boolean;
  };
  "com.pulsemcp/server-version"?: {
    source?: string;
    status?: string;
    publishedAt?: string;
    updatedAt?: string;
    isLatest?: boolean;
    // The catalog list response strips per-tool definitions from the meta blob
    // to stay small; only the lightweight auth info is kept. Full tools come
    // from getServerDetails (see `tools` below, populated by enrichment).
    "remotes[0]"?: {
      authOptions?: {
        type?: string;
      }[];
    };
  };
}

// The catalog list returns `ExternalMCPServerEntry` (no tools). `tools` is
// optional and populated client-side by enrichment (getServerDetails) for the
// add/release flow that needs per-tool URNs.
export type PulseMCPServer = Omit<ExternalMCPServerEntry, "meta"> & {
  meta: ServerMeta;
  tools?: ExternalMCPTool[];
};

// The catalog is small and the backend returns the full list in a single
// response, so a plain query over the whole catalog is enough — searching,
// sorting, and filtering all happen client-side. An optional `search` is still
// forwarded so callers that only need a specific server (e.g. the detail page)
// can narrow the response.
type CatalogQueryOptions = {
  enabled?: boolean;
  throwOnError?: boolean;
};

/**
 * How long a fetched catalog stays fresh.
 *
 * Exported because staleness is per-observer in react-query: a second reader of
 * this query that does not set it refetches the registry on every mount, even
 * though the cache entry it shares is still fresh here.
 */
export const CATALOG_STALE_TIME_MS = 5 * 60 * 1000;

function useListMCPCatalogImpl(
  search?: string,
  registryId?: string,
  options?: CatalogQueryOptions,
) {
  const client = useSdkClient();
  const projectSlug = useProjectSlugForRequests();

  return useQuery({
    queryKey: queryKeyListMCPCatalog({
      search: search || undefined,
      registryId: registryId || undefined,
      gramProject: projectSlug,
    }),
    queryFn: async () =>
      client.mcpRegistries.listCatalog({
        search: search || undefined,
        registryId: registryId || undefined,
        gramProject: projectSlug,
      }),
    staleTime: CATALOG_STALE_TIME_MS,
    enabled: options?.enabled ?? true,
    throwOnError: options?.throwOnError,
  });
}

export function useListMCPCatalog(
  search?: string,
  registryId?: string,
  // Callers that only need the catalog conditionally (e.g. only for
  // external-MCP-backed toolsets) can defer the fetch entirely rather than
  // always paying for it on mount.
  options?: CatalogQueryOptions,
): ReturnType<typeof useListMCPCatalogImpl> {
  return useListMCPCatalogImpl(search, registryId, options);
}

/**
 * Informational "already installed" check for catalog entries. Installing
 * again is always allowed and just creates another server, so this only
 * drives indicators, never blocking.
 *
 * Current installs are remote or unproxied MCP servers matched by endpoint
 * URL. Older installs are MCP servers whose toolset records the catalog
 * specifier, or a deployment attachment one of those servers still selects.
 * An attachment left behind after that server is deleted does not count.
 *
 * Lookups are keyed by project. A lookup that is still loading or has failed
 * leaves the state "unknown" instead of claiming the server is not added.
 */
export function useCatalogInstallations(projectSlug?: string): {
  installState: (server: PulseMCPServer) => CatalogInstallState;
  liveExternalMcp: (
    server: PulseMCPServer,
  ) => DeploymentExternalMCP | undefined;
} {
  const routeProject = useProjectSlugForRequests();
  const gramProject = projectSlug || routeProject;
  const projectRequest = gramProject ? { gramProject } : undefined;
  const queryOptions = { throwOnError: false, enabled: !!gramProject } as const;
  const remoteQuery = useRemoteMcpServers(
    projectRequest,
    undefined,
    queryOptions,
  );
  const unproxiedQuery = useUnproxiedMcpServers(
    projectRequest,
    undefined,
    queryOptions,
  );
  const deploymentQuery = useLatestDeployment(
    projectRequest,
    undefined,
    queryOptions,
  );
  // Server summaries carry the catalog specifier and external-MCP slugs.
  // The full toolset list includes every tool definition, which this check
  // does not need.
  const serversQuery = useMcpServers(projectRequest, undefined, queryOptions);

  const remoteUrls = useMemo(
    () =>
      normalizedUrlSet(
        (remoteQuery.data?.remoteMcpServers ?? []).map((server) => server.url),
      ),
    [remoteQuery.data?.remoteMcpServers],
  );
  const unproxiedUrls = useMemo(
    () =>
      normalizedUrlSet(
        (unproxiedQuery.data?.unproxiedMcpServers ?? []).map(
          (server) => server.url,
        ),
      ),
    [unproxiedQuery.data?.unproxiedMcpServers],
  );
  const serverEvidence = useMemo(() => {
    const originSpecifiers = new Set<string>();
    const referencedExternalMcpSlugs = new Set<string>();
    for (const server of serversQuery.data?.mcpServers ?? []) {
      const specifier = server.toolset?.registrySpecifier;
      if (specifier) originSpecifiers.add(specifier);
      for (const slug of server.toolset?.externalMcpSlugs ?? []) {
        if (slug) referencedExternalMcpSlugs.add(slug);
      }
    }
    return { originSpecifiers, referencedExternalMcpSlugs };
  }, [serversQuery.data?.mcpServers]);

  const externalMcps = deploymentQuery.data?.deployment?.externalMcps;
  const ready = useMemo(
    () => ({
      remote: remoteQuery.data !== undefined,
      unproxied: unproxiedQuery.data !== undefined,
      servers: serversQuery.data !== undefined,
      deployment: deploymentQuery.data !== undefined,
    }),
    [
      remoteQuery.data,
      unproxiedQuery.data,
      serversQuery.data,
      deploymentQuery.data,
    ],
  );
  const evidence = useMemo(
    () => ({
      remoteUrls,
      unproxiedUrls,
      originSpecifiers: serverEvidence.originSpecifiers,
      referencedExternalMcpSlugs: serverEvidence.referencedExternalMcpSlugs,
    }),
    [remoteUrls, unproxiedUrls, serverEvidence],
  );

  const installState = useCallback(
    (server: PulseMCPServer) =>
      catalogServerInstallState(server, evidence, externalMcps ?? [], ready),
    [evidence, externalMcps, ready],
  );
  const liveExternalMcp = useCallback(
    (server: PulseMCPServer) => {
      if (!ready.servers || !ready.deployment) return undefined;
      return liveCatalogExternalMcp(
        server,
        externalMcps ?? [],
        evidence.referencedExternalMcpSlugs,
      );
    },
    [
      evidence.referencedExternalMcpSlugs,
      externalMcps,
      ready.deployment,
      ready.servers,
    ],
  );

  return { installState, liveExternalMcp };
}
